import { Component, For, createMemo } from 'solid-js';
import type { EditableStop, TextureType } from '../../types/heatmap.types';
import {
  GRADIENT_STOP_BAND_OFFSET_PX,
  GRADIENT_STOP_BAR_WIDTH_PX,
  GRADIENT_STOP_COLUMN_WIDTH_PX,
  heatmapEditorBarCss,
  stopHandleWrapperTopPx,
  stopListHeightPx,
  VIEWER_STOP_LAYOUT,
} from '../../heatmap-stop-layout';
import { transparencyCheckerboardStyle } from '../../gradient-preview/heatmap-css';

/** Color Indicator overlay — Figma Frame 15783 / Ellipse 344 + Line 11. */
const HANDLE_WRAPPER_LEFT_PX = 6;
const HANDLE_WRAPPER_WIDTH_PX = 37;
const HANDLE_WRAPPER_HEIGHT_PX = 10;
const HANDLE_GAP_PX = 3;
const HANDLE_SIZE_PX = 10;
const HANDLE_BORDER_PX = 2;
const HANDLE_LINE_WIDTH_PX = 24;
const HANDLE_LINE_LEFT_PX = HANDLE_SIZE_PX + HANDLE_GAP_PX;
const HANDLE_CIRCLE_TOP_OFFSET_PX = 2;
const HANDLE_Z_INDEX = 3;

const HANDLE_STROKE_COLOR = '#FFFFFF';
const HANDLE_DROP_SHADOW = 'drop-shadow(0px 0px 1.8px rgba(0, 0, 0, 0.72))';

interface GradientStopBarProps {
  readonly stops: readonly EditableStop[];
  readonly textureType: TextureType;
  readonly colorAboveRange?: string;
  readonly colorBelowRange?: string;
}

interface StopHandleIndicatorProps {
  readonly topPx: number;
  readonly color: string;
  readonly testId: string;
}

const StopHandleIndicator: Component<StopHandleIndicatorProps> = (props) => (
  <div
    class="absolute pointer-events-none"
    style={{
      left: `${String(HANDLE_WRAPPER_LEFT_PX)}px`,
      top: `${String(props.topPx)}px`,
      width: `${String(HANDLE_WRAPPER_WIDTH_PX)}px`,
      height: `${String(HANDLE_WRAPPER_HEIGHT_PX)}px`,
      filter: HANDLE_DROP_SHADOW,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'z-index': HANDLE_Z_INDEX,
    }}
    data-testid={props.testId}
  >
    <div
      class="absolute left-0 shrink-0 rounded-full box-border"
      style={{
        top: `${String(HANDLE_CIRCLE_TOP_OFFSET_PX)}px`,
        width: `${String(HANDLE_SIZE_PX)}px`,
        height: `${String(HANDLE_SIZE_PX)}px`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'background-color': props.color,
        border: `${String(HANDLE_BORDER_PX)}px solid ${HANDLE_STROKE_COLOR}`,
      }}
    />
    <div
      class="absolute shrink-0 box-border"
      style={{
        left: `${String(HANDLE_LINE_LEFT_PX)}px`,
        top: `${String(GRADIENT_STOP_BAND_OFFSET_PX)}px`,
        width: `${String(HANDLE_LINE_WIDTH_PX)}px`,
        height: '0px',
        border: 'none',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'border-top': `${String(HANDLE_BORDER_PX)}px solid ${HANDLE_STROKE_COLOR}`,
      }}
      data-testid={`${props.testId}-line`}
    />
  </div>
);

export const GradientStopBar: Component<GradientStopBarProps> = (props) => {
  const editableStopCount = (): number => props.stops.length;

  const barHeightPx = (): number => stopListHeightPx(editableStopCount());

  const overflowOverrides = (): {
    above?: string;
    below?: string;
  } => ({
    above: props.colorAboveRange,
    below: props.colorBelowRange,
  });

  const barBackground = createMemo(() =>
    heatmapEditorBarCss(props.stops, props.textureType, overflowOverrides())
  );

  function handleTopPx(stop: EditableStop): number {
    return stopHandleWrapperTopPx(stop, props.stops, props.textureType, {
      ...VIEWER_STOP_LAYOUT,
      textureType: props.textureType,
    });
  }

  return (
    <div
      class="relative shrink-0"
      style={{
        width: `${String(GRADIENT_STOP_COLUMN_WIDTH_PX)}px`,
        height: `${String(barHeightPx())}px`,
        isolation: 'isolate',
      }}
      data-testid="gradient-stop-bar"
    >
      <div
        class="absolute left-0 top-0 overflow-hidden border border-[var(--modus-wc-color-base-200,#e0e1e9)]"
        style={{
          width: `${String(GRADIENT_STOP_BAR_WIDTH_PX)}px`,
          height: `${String(barHeightPx())}px`,
          // Prototype uses 4px — do not use Tailwind `rounded` (50% in tc.styles.css).
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'border-radius': '4px',
        }}
        data-testid="gradient-stop-bar-track"
      >
        <div
          class="absolute inset-0"
          style={transparencyCheckerboardStyle()}
          aria-hidden="true"
        />
        <div
          class="absolute inset-0"
          style={{ background: barBackground() }}
          aria-hidden="true"
        />
      </div>

      <For each={props.stops}>
        {(stop, index) => (
          <StopHandleIndicator
            topPx={handleTopPx(stop)}
            color={stop.color}
            testId={`gradient-stop-handle-${String(index())}`}
          />
        )}
      </For>
    </div>
  );
};
